import { isEmpty, isNil } from 'lodash';
import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { Button, Row, Col, Stack } from 'react-bootstrap';
import InfiniteScroll from 'react-infinite-scroll-component';

import { Loading } from '../../../BayPolotnoEditor/Loading';

interface TextPanelProps {
	store: any;
	api: any;
}

interface TextElement {
	type: string;
	name: string;
	text: string;
	fontSize: number;
	fontFamily: string;
	fontWeight: string;
	fill: string;
	align: string;
	width?: number;
}

interface HeadlineTemplate {
	name: string;
	json: {
		width: number;
		height: number;
		pages: Array<{
			children: any[];
		}>;
	};
	source?: { data?: any };
	rasters: Array<{
		url: string;
	}>;
}

const textData: Record<string, TextElement> = {
	textHeader: {
		type: 'text',
		name: '',
		text: 'Header',
		fontSize: 70,
		fontFamily: 'Roboto',
		fontWeight: 'bold',
		fill: 'rgba(0,0,0,1)',
		align: 'center',
	},
	textTitle: {
		type: 'text',
		name: '',
		text: 'Title',
		fontSize: 50,
		fontFamily: 'Roboto',
		fontWeight: 'normal',
		fill: 'rgba(0,0,0,1)',
		align: 'center',
	},
	textBody: {
		type: 'text',
		name: '',
		text: 'Body',
		fontSize: 30,
		fontFamily: 'Roboto',
		fontWeight: 'normal',
		fill: 'rgba(0,0,0,1)',
		align: 'center',
	},
};

const getRatio = (A: number, B: number): number => {
	return Math.abs(1 - (A - B) / A);
};

const createElementID = (): string => {
	let id = '';
	for (let i = 0; i <= 11; i++) {
		id +=
			'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'.charAt(
				Math.floor(Math.random() * 63),
			);
	}

	return id;
};

export const TextPanel: React.FC<TextPanelProps> = observer(({ store, api }) => {
	const [headlines, setHeadlines] = React.useState<HeadlineTemplate[]>();
	const [nextPage, setNextPage] = React.useState<any>();

	const location = window.location;

	const handleRead = (queryParams?: any, limit?: number, offset?: number) => {
		queryParams = queryParams || {
			variant: 'headline',
			'owner.idNumber': 0,
			status: 'active',
		};

		limit = limit || 24;
		offset = offset || 0;

		api?.templates
			.read({ templateId: null, queryParams, limit, offset })
			.then((resp: any) => {
				if (resp.meta.nextPage) {
					setNextPage(resp.meta.nextPage.searchParams);
				} else {
					setNextPage(null);
				}

				if (headlines) {
					setHeadlines([...headlines, ...resp.result]);
				} else {
					setHeadlines(resp.result);
				}
			})
			.catch((err: any) => {
				console.error(err);
			});
	};

	const handleHeadlineSelect = (headlineData: HeadlineTemplate['json']) => {
		const children = headlineData?.pages?.[0]?.children;
		if (!Array.isArray(children)) {
			return;
		}

		const ids: string[] = [];

		store.history.transaction(async () => {
			children.forEach((child: any) => {
				const id = createElementID();
				const ratioWidth = getRatio(headlineData.width, store.width);
				const ratioHeight = getRatio(headlineData.height, store.height);

				ids.push(id);

				child.id = id;
				child.width = child.width * ratioWidth;
				child.height = child.height * ratioHeight;
				child.fontSize = child.fontSize * ratioHeight;
				child.x = child.x * ratioWidth;
				child.y = child.y * ratioHeight;

				store.activePage?.addElement(child);
			});
		});

		store.selectElements(ids);
	};

	const addTextToPage = async (type: string) => {
		store.history.transaction(async () => {
			let text: Partial<TextElement> = {};

			switch (type) {
			case 'header':
				text = textData.textHeader;
				break;

			case 'title':
				text = textData.textTitle;
				break;

			case 'body':
				text = textData.textBody;
				break;

			default:
				break;
			}

			if (!isEmpty(text)) {
				text.width = store.width / 2;
				store.activePage?.addElement(text);
			}
		});
	};

	useEffect(() => {
		//make sure we get the menu if it is not already in the global state
		if (isNil(headlines) && location.pathname.indexOf('headline') === -1) {
			handleRead();
		}
	}, [headlines, location.pathname]);

	return (
		<div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
			<div>
				<Stack className="mb-3" direction="vertical" gap={1}>
					<Button
						className="d-block fs-3"
						variant="link text-decoration-none text-white"
						onClick={() => addTextToPage('header')}
					>
						Insert Header Text
					</Button>
					<Button
						className="d-block fs-4"
						variant="link text-decoration-none text-white"
						onClick={() => addTextToPage('title')}
					>
						Insert Title Text
					</Button>
					<Button
						className="d-block fs-5"
						variant="link text-decoration-none text-white"
						onClick={() => addTextToPage('body')}
					>
						Insert Body Text
					</Button>
				</Stack>

				{isNil(headlines) && location.pathname.indexOf('headline') === -1 ? (
					<div>Loading</div>
				) : (
					!isNil(headlines) &&
					headlines.length > 0 && (
						<InfiniteScroll
							className="container-fluid"
							dataLength={headlines?.length} //This is important field to render the next data
							hasMore={!isNil(nextPage)}
							loader={<Loading message="Loading headlines" />}
							next={() =>
								handleRead(nextPage.q, nextPage.limit, nextPage.offset)
							}
							style={{ overflowX: 'hidden' }}
						>
							<Row>
								{headlines.length > 0
									? headlines.map((item, index) => (
										<Col key={index} xs={6}>
											<img
												alt={item.name}
												className="w-100 rounded-2 cursor-pointer"
												src={item.rasters[0].url}
												style={{ filter: 'invert(1)' }}
												onClick={() => handleHeadlineSelect(item.json ?? item.source?.data)}
											/>
										</Col>
									))
									: null}
							</Row>
						</InfiniteScroll>
					)
				)}
			</div>
		</div>
	);
});

export default TextPanel;
